import { Alert, Divider, Space, Spin, Steps, Typography } from 'antd';
import { message } from '@/components/Message';
import React, { useEffect, useState } from 'react';
import { useImmer } from 'use-immer';
import { addWsListener, DOMAIN_INTERACT_OUTSIDE, removeWsListener, WsActions } from '../../../api/ws';
import PosButton from '@/components/PosButton';
import { addGlobalScanListener, removeGlobalScanListener } from '@/components/QRScanner/GlobalScanner';
import { assignOrderToUser, AssignOrderToUserResult, Order } from '@/models/order';
import { PaymentChannel } from '@/models/payment';
import { fetchSyncUnionIdByTransactionId, fetchUserBasicInfo, User } from '@/models/user';
import { playSound } from '@/utils/sound';

type Props = {
  user: User;
  order?: Order;
  onDataChange?: (user?: User, order?: Order) => void;
};
/**
 * 客户识别
 * 1. 有 unionId
 * 2. 没有 unionId - 微信支付
 * 3. 没有 unionId - 其它支付
 * @param param0
 * @returns
 */
const UserConnector: React.FC<Props> = ({ user: propsUser, order, onDataChange }: Props) => {
  const [user, updateUser] = useImmer(propsUser);
  const [receivedUserMap, updateReceivedUserMap] = useImmer<{ [id: string]: User }>({});
  const [loading, setLoading] = useState(false);
  const hasUnionId = Boolean(user.weixinUnionid);
  const hasWechatPayId = Boolean(order.payId) && order.payChannel === PaymentChannel.Tenpay;

  const submitUpdateUser = (updatedUser: User, updatedOrder?: Order) => {
    message.success('客户留存成功');
    updateUser(() => updatedUser);
    onDataChange(updatedUser, updatedOrder);
  };

  // 微信支付，更新 unionId
  const syncUnionIdByTransactionId = async () => {
    setLoading(true);
    const updatedUser = await fetchSyncUnionIdByTransactionId({
      payId: order.payId,
      username: user.username,
    });
    setLoading(false);
    submitUpdateUser(updatedUser);
  };

  // 其它支付，归属到新用户
  const assignToReceivedUser = async (receivedUser: User) => {
    const {
      code,
      user: updatedUser,
      order: updatedOrder,
    } = await assignOrderToUser({
      orderSn: order.orderSn,
      username: receivedUser.username,
    });
    if (code >= 200) {
      submitUpdateUser(updatedUser, updatedOrder);
    } else {
      message.error(`失败: ${AssignOrderToUserResult[code]}`);
    }
  };

  useEffect(() => {
    // 没有 unionid, 有微信支付id
    if (!hasUnionId && hasWechatPayId) {
      syncUnionIdByTransactionId();
    }
  }, [user, order, hasWechatPayId, hasUnionId]);

  // 接收二维码+联动标签
  useEffect(() => {
    const onReceivedUser = async (username: string) => {
      if (receivedUserMap[username]) {
      } else {
        const receivedUser = await fetchUserBasicInfo(username);
        updateReceivedUserMap(draft => {
          draft[username] = receivedUser;
        });
      }
    };

    const scan1 = addGlobalScanListener('user', ({ code: value }) => {
      playSound('scan_di');
      // helpers.showUserModal({ userName: value });
      onReceivedUser(value);
      return true;
    });

    const ws1 = addWsListener<{ username: string; displayName: string }>(
      DOMAIN_INTERACT_OUTSIDE,
      WsActions.WEAPP_USER_VISIT_IN_POI,
      payload => {
        if (!payload || !payload.username) return;
        playSound('notification');
        onReceivedUser(payload.username);
        return true;
      }
    );

    return () => {
      removeGlobalScanListener(scan1);
      removeWsListener(ws1);
    };
  }, [receivedUserMap]);

  return (
    <>
      {!user.wecomId && (
        <Alert
          showIcon
          type="error"
          message={
            <>
              请提示该客户扫码添加<span className="emphasize red">企微</span>
            </>
          }
          // description={
          //   <Space direction="vertical" size={0}>
          //     <div>1. 后续会通过微信为您发送护理建议和复诊提醒</div>
          //     <div>2. 稍候我把注意事项和护理建议发送给您</div>
          //     <div>3. 您可以详细查看检测报告</div>
          //     <div>4. 有不定期的优惠活动通知</div>
          //   </Space>
          // }
          style={{ marginBottom: 5, marginTop: 5 }}
        />
      )}

      {!hasUnionId && (
        <Alert
          style={{ marginTop: 5 }}
          showIcon
          type={'error'}
          message={<Typography.Text strong>客户未留存</Typography.Text>}
          description={
            <div>
              该客户尚未完成身份识别，请视情况从以下方式选择其一进行留存
              <Divider />
              <div>
                <Steps
                  direction="vertical"
                  items={[
                    hasWechatPayId
                      ? {
                          status: 'process' as const,
                          title: '支付账户留存',
                          description: (
                            <div>
                              <PosButton
                                loading={loading}
                                onClick={async () => {
                                  syncUnionIdByTransactionId();
                                }}>
                                留存到当前支付客户
                              </PosButton>
                            </div>
                          ),
                        }
                      : null,
                    {
                      status: 'process' as const,
                      title: '客户识别留存',
                      description: (
                        <div>
                          <Typography.Title level={5}>
                            <Space>
                              <Spin size={'small'} />
                              <span>等待识别客户身份</span>
                            </Space>
                          </Typography.Title>
                          <div>
                            {Object.values(receivedUserMap).map(user => {
                              return (
                                <div className="space-between" key={user.id}>
                                  <span>{user.username.slice(0, 5)}***</span>
                                  <PosButton
                                    onClick={async () => {
                                      await assignToReceivedUser(user);
                                    }}>
                                    留存到该账号
                                  </PosButton>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ),
                    },
                    {
                      status: 'process' as const,
                      title: '手动选择',
                      description: (
                        <div>
                          <PosButton>TODO: 关联到存量客户?</PosButton>
                        </div>
                      ),
                    },
                  ].filter(Boolean)}
                />
              </div>
            </div>
          }
        />
      )}
    </>
  );
};

export default UserConnector;
